import React, { useState } from 'react';
import { TeamInLeague, TrainingFocus, Position } from '../../domain/entities';
import { AdPlaceholder } from '../shared/AdPlaceholder';
import { Modal } from '../shared/Modal';
import { useToast } from '../../contexts/ToastContext';
import { soundService } from '../../services/SoundService';
import { TRAINING_FACILITIES_LEVELS } from '../../domain/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDumbbell, faArrowUp, faArrowDown } from '@fortawesome/free-solid-svg-icons';
import { TrainingTeamPanel } from './training/TrainingTeamPanel';
import { TrainingPositionalPanel } from './training/TrainingPositionalPanel';
import { TrainingIndividualPanel } from './training/TrainingIndividualPanel';
import { TrainingHistoryPanel } from './training/TrainingHistoryPanel';
import { CollapsibleCard } from '../shared/CollapsibleCard';
import { TrainingFacilitiesHelpModal } from '../shared/TrainingFacilitiesHelpModal';
import { View } from '../../App';
import './TrainingScreen.css';
import './training/TrainingPanels.css';

interface TrainingScreenProps {
    team: TeamInLeague;
    pushView: (view: View) => void;
    onUpdateTeamTrainingFocus: (focus: TrainingFocus) => void;
    onUpdatePositionalTrainingFocus: (position: Position, focus: TrainingFocus | null) => void;
    onUpdatePlayerTrainingFocus: (playerId: number, focus: TrainingFocus | null) => void;
    onUpgradeTrainingFacilities: () => Promise<{ success: boolean, message: string }>;
    onDowngradeTrainingFacilities: () => Promise<{ success: boolean, message: string }>;
    adsEnabled: boolean;
}

export const TrainingScreen: React.FC<TrainingScreenProps> = (props) => {
    const { team, onUpgradeTrainingFacilities, onDowngradeTrainingFacilities, adsEnabled } = props;
    const { addToast } = useToast();
    const [activeTab, setActiveTab] = useState<'focus' | 'history'>('focus');
    const [modalAction, setModalAction] = useState<'upgrade' | 'downgrade' | null>(null);
    const [isHelpOpen, setIsHelpOpen] = useState(false);

    const currentLevelInfo = TRAINING_FACILITIES_LEVELS[team.trainingFacilities.level - 1];
    const nextLevelInfo = TRAINING_FACILITIES_LEVELS[team.trainingFacilities.level] || null;

    const handleAction = async (action: 'upgrade' | 'downgrade') => {
        soundService.playClick();
        const result = action === 'upgrade' ? await onUpgradeTrainingFacilities() : await onDowngradeTrainingFacilities();
        if (result.success) {
            soundService.playSuccess();
            addToast(result.message, 'success');
        } else {
            soundService.playError();
            addToast(result.message, 'error');
        }
        setModalAction(null);
    };

    return (
        <>
            <div className="main-content training-screen">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h1><FontAwesomeIcon icon={faDumbbell} style={{ marginRight: '8px', color: 'var(--positive-color)' }} />Training Facilities</h1>
                    <button
                        className="button"
                        onClick={() => setIsHelpOpen(true)}
                        style={{ padding: '8px 12px', fontSize: '0.9em' }}
                        title="Help"
                    >
                        Help
                    </button>
                </div>
                <p style={{ marginBottom: '24px', opacity: 0.9 }}>
                    Upgrade your training facilities to improve player development and skill progression.
                </p>

                {/* Current Training Facilities Info */}
                <CollapsibleCard
                    cardId="training-facilities-info"
                    title={`${currentLevelInfo.name} - Level ${currentLevelInfo.level}`}
                    defaultCollapsed={false}
                >
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                        <div style={{ padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px' }}>
                            <div style={{ fontSize: '0.9em', color: 'var(--accent-text-color)', marginBottom: '4px' }}>Weekly Cost</div>
                            <div style={{ fontSize: '1.4em', fontWeight: 'bold' }}>£{currentLevelInfo.weeklyWages.toLocaleString()}</div>
                            <div style={{ fontSize: '0.85em', marginTop: '4px' }}>Wages deducted each week</div>
                        </div>
                        <div style={{ padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px' }}>
                            <div style={{ fontSize: '0.9em', color: 'var(--accent-text-color)', marginBottom: '4px' }}>Current Level</div>
                            <div style={{ fontSize: '1.4em', fontWeight: 'bold' }}>{currentLevelInfo.level}</div>
                            <div style={{ fontSize: '0.85em', marginTop: '4px' }}>Training effectiveness rating</div>
                        </div>
                    </div>

                    <div style={{ padding: '12px', backgroundColor: 'rgba(0, 255, 155, 0.1)', borderRadius: '4px', marginBottom: '16px' }}>
                        <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>What this facility provides:</p>
                        <ul style={{ margin: '0', paddingLeft: '20px' }}>
                            <li>Improved player skill development and training efficiency</li>
                            <li>Better recovery rates for injured players</li>
                            <li>Enhanced tactical training focus effects</li>
                            <li>Higher levels = faster skill progression & better training outcomes</li>
                        </ul>
                    </div>

                    {/* Upgrade/Downgrade Controls */}
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                        {currentLevelInfo.level > 1 && (
                            <button
                                className="button"
                                onClick={() => setModalAction('downgrade')}
                                style={{ flex: 1 }}
                            >
                                <FontAwesomeIcon icon={faArrowDown} style={{ marginRight: '6px' }} />Downgrade to {TRAINING_FACILITIES_LEVELS[currentLevelInfo.level - 2]?.name || 'Previous Level'}
                            </button>
                        )}
                        {nextLevelInfo && (
                            <button
                                className="button"
                                onClick={() => setModalAction('upgrade')}
                                style={{ flex: 1 }}
                                disabled={team.finances.balance < nextLevelInfo.upgradeCost}
                            >
                                <FontAwesomeIcon icon={faArrowUp} style={{ marginRight: '6px' }} />Upgrade to {nextLevelInfo.name}
                            </button>
                        )}
                    </div>

                    {/* Upgrade/Downgrade Cost Info */}
                    {nextLevelInfo && (
                        <div style={{ padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px', fontSize: '0.9em', marginBottom: '12px' }}>
                            <strong>Upgrade Cost:</strong> £{nextLevelInfo.upgradeCost.toLocaleString()}
                            <div style={{ marginTop: '8px', fontSize: '0.85em', color: 'var(--accent-text-color)' }}>
                                Upgrading to {nextLevelInfo.name} increases weekly cost to £{nextLevelInfo.weeklyWages.toLocaleString()}/wk
                            </div>
                        </div>
                    )}
                    {currentLevelInfo.level > 1 && (
                        <div style={{ padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px', fontSize: '0.9em' }}>
                            <strong>💰 Save Weekly Wages:</strong> Downgrading reduces your weekly staff cost
                            <div style={{ marginTop: '8px', fontSize: '0.85em', color: 'var(--positive-color)' }}>
                                Downgrading to {TRAINING_FACILITIES_LEVELS[currentLevelInfo.level - 2]?.name} saves £{(currentLevelInfo.weeklyWages - TRAINING_FACILITIES_LEVELS[currentLevelInfo.level - 2]?.weeklyWages).toLocaleString()}/wk
                            </div>
                        </div>
                    )}
                </CollapsibleCard>

                {adsEnabled && <AdPlaceholder />}

                <div className="tabs">
                    <button className={`tab-button ${activeTab === 'focus' ? 'active' : ''}`} onClick={() => setActiveTab('focus')}>Focus</button>
                    <button className={`tab-button ${activeTab === 'history' ? 'active' : ''}`} onClick={() => setActiveTab('history')}>History</button>
                </div>

                {activeTab === 'focus' && (
                    <div className="training-focus-container">
                        <TrainingTeamPanel {...props} />
                        <TrainingPositionalPanel {...props} />
                        <TrainingIndividualPanel {...props} facilitiesLevel={team.trainingFacilities.level} />
                    </div>
                )}

                {activeTab === 'history' && (
                    <TrainingHistoryPanel team={team} pushView={props.pushView} />
                )}
            </div>
            
            {modalAction && (
                <Modal isOpen={!!modalAction} onClose={() => setModalAction(null)}>
                    {modalAction === 'upgrade' && nextLevelInfo && (
                        <>
                            <h2>Confirm Facility Upgrade</h2>
                            <p>Are you sure you want to upgrade your training facilities to <strong>{nextLevelInfo.name}</strong>?</p>
                            <p>This will cost <strong className="negative">£{nextLevelInfo.upgradeCost.toLocaleString()}</strong> and increase weekly staff wages to <strong className="negative">£{nextLevelInfo.weeklyWages.toLocaleString()}</strong>.</p>
                            <div className="modal-actions">
                                <button className="button" onClick={() => setModalAction(null)}>Cancel</button>
                                <button className="button primary" onClick={() => handleAction('upgrade')}>Confirm</button>
                            </div>
                        </>
                    )}
                    {modalAction === 'downgrade' && (
                         <>
                            <h2>Confirm Facility Downgrade</h2>
                            <p>Are you sure you want to downgrade your training facilities?</p>
                            <p className="negative">This will reduce weekly wage costs but will negatively impact player development. You will have to pay the upgrade fee again to return to this level.</p>
                            <div className="modal-actions">
                                <button className="button" onClick={() => setModalAction(null)}>Cancel</button>
                                <button className="button danger" onClick={() => handleAction('downgrade')}>Confirm</button>
                            </div>
                        </>
                    )}
                </Modal>
            )}

            <TrainingFacilitiesHelpModal isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} />
        </>
    );
};
